import { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import {
  TrendingUp,
  Sparkles,
  Heart,
  BookOpen,
  Calendar,
  ArrowRight,
  Clock,
  User,
  Tag,
  Bookmark
} from 'lucide-react';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:5000'
  : 'https://kiyafet-magazasi-backend.vercel.app';

interface Article {
  id: number;
  baslik: string;
  aciklama?: string;
  ozet?: string;
  tip: string;
  kategori?: string;
  resim?: string;
  link?: string;
  videoUrl?: string;
  yazar?: string;
  yazarAvatar?: string;
  okumaSuresi?: number;
  etiketler?: string[];
  oneCikan?: boolean;
  aktif?: boolean;
  sira?: number;
  createdAt?: string;
}

export function Explore() {
  const [content, setContent] = useState<Article[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [bookmarkedIds, setBookmarkedIds] = useState<number[]>([]);
  const [visibleCount, setVisibleCount] = useState(6);

  useEffect(() => {
    fetch(`${API_URL}/api/kesfet`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setContent(data); })
      .catch(() => {});
  }, []);

  // Load bookmarks from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('ladiora_bookmarks');
      if (saved) setBookmarkedIds(JSON.parse(saved));
    } catch {}
  }, []);

  const categories = [
    { id: 'all', name: 'Tümü', icon: Sparkles },
    { id: 'trendler', name: 'Trendler', icon: TrendingUp },
    { id: 'stil-rehberi', name: 'Stil Rehberi', icon: BookOpen },
    { id: 'bakim-guzellik', name: 'Bakım & Güzellik', icon: Heart },
    { id: 'kampanyalar', name: 'Kampanyalar', icon: Tag },
    { id: 'hikayeler', name: 'Hikayeler', icon: User },
  ];

  const filteredArticles = selectedCategory === 'all'
    ? content
    : content.filter(a => a.kategori === selectedCategory);

  const featuredArticle = content.find(a => a.oneCikan);
  const regularArticles = filteredArticles.filter(a => !a.oneCikan);
  const visibleArticles = regularArticles.slice(0, visibleCount);
  const hasMore = regularArticles.length > visibleCount;

  const toggleBookmark = (id: number) => {
    setBookmarkedIds(prev => {
      const next = prev.includes(id) ? prev.filter(b => b !== id) : [...prev, id];
      localStorage.setItem('ladiora_bookmarks', JSON.stringify(next));
      return next;
    });
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return ''; }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-pink-600 to-red-600 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center text-white">
              <h1 className="text-5xl md:text-6xl font-bold mb-4">Keşfet</h1>
              <p className="text-xl md:text-2xl text-white/90 max-w-2xl mx-auto">
                Moda, stil, güzellik ve yaşam tarzı hakkında ilham verici içerikler
              </p>
            </div>
          </div>
        </div>

        {/* Category Filter - Sticky */}
        <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex gap-2 overflow-x-auto py-4 scrollbar-hide">
              {categories.map(category => {
                const Icon = category.icon;
                return (
                  <button
                    key={category.id}
                    onClick={() => { setSelectedCategory(category.id); setVisibleCount(6); }}
                    className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold whitespace-nowrap transition-all ${
                      selectedCategory === category.id
                        ? 'bg-gradient-to-r from-pink-600 to-red-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="size-5" />
                    {category.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 py-12">
          {/* Featured Article */}
          {selectedCategory === 'all' && featuredArticle && (
            <>
              <div className="mb-12">
                <div className="flex items-center gap-2 mb-6">
                  <Sparkles className="size-6 text-pink-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Öne Çıkan</h2>
                </div>

                <div className="relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all group">
                  <div className="grid md:grid-cols-2 gap-0">
                    <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden">
                      <ImageWithFallback
                        src={featuredArticle.resim || ''}
                        alt={featuredArticle.baslik}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="inline-block bg-gradient-to-r from-pink-600 to-red-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                          ⭐ Öne Çıkan
                        </span>
                      </div>
                      <button
                        onClick={() => toggleBookmark(featuredArticle.id)}
                        className="absolute top-4 right-4 size-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors"
                      >
                        <Bookmark className={`size-5 ${bookmarkedIds.includes(featuredArticle.id) ? 'fill-pink-600 text-pink-600' : 'text-gray-700'}`} />
                      </button>
                    </div>

                    <div className="p-8 md:p-12 flex flex-col justify-center">
                      <div className="flex items-center gap-4 mb-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Calendar className="size-4" />
                          {formatDate(featuredArticle.createdAt)}
                        </span>
                        {featuredArticle.okumaSuresi && (
                          <span className="flex items-center gap-1">
                            <Clock className="size-4" />
                            {featuredArticle.okumaSuresi} dk
                          </span>
                        )}
                      </div>

                      <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                        {featuredArticle.baslik}
                      </h3>

                      <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                        {featuredArticle.ozet || featuredArticle.aciklama}
                      </p>

                      {featuredArticle.etiketler && featuredArticle.etiketler.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-6">
                          {featuredArticle.etiketler.map(tag => (
                            <span key={tag} className="inline-block bg-pink-50 text-pink-700 px-3 py-1 rounded-full text-sm font-medium">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        {featuredArticle.link && (
                          <a href={featuredArticle.link} className="group/btn flex items-center gap-2 bg-gradient-to-r from-pink-600 to-red-600 text-white px-6 py-3 rounded-full font-semibold hover:shadow-lg transition-all ml-auto">
                            Devamını Oku
                            <ArrowRight className="size-5 group-hover/btn:translate-x-1 transition-transform" />
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                  <TrendingUp className="size-6 text-pink-600" />
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                </div>
              </div>
            </>
          )}

          {/* Articles Grid */}
          {visibleArticles.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {visibleArticles.map(article => (
                <article key={article.id} className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all group">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <ImageWithFallback
                      src={article.resim || ''}
                      alt={article.baslik}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <button
                      onClick={() => toggleBookmark(article.id)}
                      className="absolute top-3 right-3 size-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors"
                    >
                      <Bookmark className={`size-4 ${bookmarkedIds.includes(article.id) ? 'fill-pink-600 text-pink-600' : 'text-gray-700'}`} />
                    </button>
                    <div className="absolute bottom-3 left-3">
                      <span className="inline-block bg-white/90 backdrop-blur-sm text-gray-900 px-3 py-1 rounded-full text-xs font-semibold">
                        {categories.find(cat => cat.id === article.kategori)?.name || article.tip}
                      </span>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <Calendar className="size-3" />
                        {formatDate(article.createdAt)}
                      </span>
                      {article.okumaSuresi && (
                        <span className="flex items-center gap-1">
                          <Clock className="size-3" />
                          {article.okumaSuresi} dk
                        </span>
                      )}
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight group-hover:text-pink-600 transition-colors">
                      {article.baslik}
                    </h3>

                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {article.ozet || article.aciklama}
                    </p>

                    {article.etiketler && article.etiketler.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {article.etiketler.slice(0, 2).map(tag => (
                          <span key={tag} className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded-md text-xs font-medium">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div />
                      {article.link ? (
                        <a href={article.link} className="flex items-center gap-1 text-pink-600 font-semibold text-sm hover:gap-2 transition-all">
                          Oku
                          <ArrowRight className="size-4" />
                        </a>
                      ) : (
                        <span className="flex items-center gap-1 text-pink-600 font-semibold text-sm">
                          Oku
                          <ArrowRight className="size-4" />
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : content.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-gray-500 text-xl">Yakında burada ilham verici içerikler olacak</p>
            </div>
          ) : (
            <div className="py-20 text-center">
              <p className="text-gray-500 text-xl">Bu kategoride henüz içerik yok</p>
            </div>
          )}

          {/* Load More */}
          {hasMore && (
            <div className="mt-12 text-center">
              <button
                onClick={() => setVisibleCount(prev => prev + 6)}
                className="inline-flex items-center gap-2 bg-white border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-full font-semibold hover:border-pink-600 hover:text-pink-600 transition-all"
              >
                Daha Fazla Yükle
                <ArrowRight className="size-5" />
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
